import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Bot, Info } from 'lucide-react'
import { type KBSettings } from '@/hooks/useSettings'

interface CustomAgentSettingsProps {
  formData: KBSettings
  handleChange: (field: keyof KBSettings, value: any) => void
}

export function CustomAgentSettings({ formData, handleChange }: CustomAgentSettingsProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-primary" />
          Custom Agent Settings
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-blue-900 dark:text-blue-100 text-sm">
              Custom agents are an experimental feature managed by the Chatterpillar team. 
              Advanced customization options will be available in future versions.
            </AlertDescription>
          </Alert>

          {/* Enable Agent Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="agent">Enable Custom Agent</Label>
              <p className="text-xs text-muted-foreground">
                Use a custom RAG agent instead of the standard chat assistant
              </p>
            </div>
            <Switch
              id="agent"
              checked={formData.agent}
              onCheckedChange={(checked) => handleChange('agent', checked)}
            />
          </div>

          {/* Agent ID Input (shown when agent is enabled) */}
          {formData.agent && (
            <div className="space-y-2 pl-4 border-l-2 border-primary/20">
              <Label htmlFor="agent_id">
                Agent ID <span className="text-red-500">*</span>
              </Label>
              <Input
                id="agent_id"
                type="text"
                value={formData.agent_id}
                onChange={(e) => handleChange('agent_id', e.target.value)}
                placeholder="e.g., 47b8a25bcf4e11f08177bb8944d69d70"
                className={!formData.agent_id.trim() ? 'border-red-300' : ''}
              />
              <p className="text-xs text-muted-foreground">
                Enter the custom agent ID provided by the Chatterpillar team
              </p>
              {!formData.agent_id.trim() && (
                <p className="text-xs text-red-500">
                  Agent ID is required when custom agent is enabled
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
